/**
 * The header bell: a badge with the open warning + critical alerts (red when
 * any is critical), polled every minute; opens a menu of the latest alerts.
 * Only for whoever may see alerts.
 */

import { useState } from 'react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Bell } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui';
import { useAuth } from '@/hooks';
import { cn } from '@/lib/utils';

import { useAlertCounts } from '../hooks/useAlerts';
import { AlertBellMenu } from './AlertBellMenu';

const MENU = cn(
  'z-50 w-[min(20rem,calc(100vw-2rem))] rounded-xl border bg-popover p-1.5 shadow-lg',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
  'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
);

/** The count on the bell: red with any critical alert, amber otherwise. */
function Badge({ count, critical }: { count: number; critical: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        'absolute right-1 top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none tabular-nums',
        critical ? 'bg-destructive text-destructive-foreground' : 'bg-warning text-warning-foreground',
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function AlertBell() {
  const { t } = useTranslation('alerts');
  const { hasPermission } = useAuth();
  const { data } = useAlertCounts();
  const [open, setOpen] = useState(false);
  if (!hasPermission('alerts.view')) return null;
  const urgent = (data?.warning ?? 0) + (data?.critical ?? 0);
  const label = urgent ? t('bell.labelCount', { count: urgent }) : t('bell.label');
  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger asChild>
        <Button variant="ghost" size="icon" aria-label={label} title={label} className="relative overflow-visible">
          <Bell className="h-4 w-4" aria-hidden />
          {urgent ? <Badge count={urgent} critical={Boolean(data?.critical)} /> : null}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className={MENU} sideOffset={8} align="end">
          <AlertBellMenu open={open} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
